/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { forwardRef } from 'react';

import { IntrinsicBox } from './IntrinsicBox';

import { Box } from '.';

import type { ElementType, Ref } from 'react';
import type { IntrinsicBoxProps } from '.';

export type IntrinsicFlexProps<As extends ElementType = 'div'> =
  IntrinsicBoxProps<As>;

/**
 * Use the IntrinsicFlex component like a ThemeUI Flex, but
 * with a `ratio` prop like ThemeUI AspectRatio.
 *
 * The differences between this component and AspectRatio are:
 *
 * - AspectRatio hides overflow (and thus requires content to be positioned),
 *   while this component shows overflow, and content is allowed to flow.
 *
 * - AspectRatio only supports one `ratio` value, while this component supports
 *   multiple for responsive designs
 *
 * - It supports `minWidth` and `maxWidth` props, which can also be responsive
 *
 * @example
 *   <IntrinsicFlex ratio={278 / 178} p={4} bg="primary">
 *     Beep
 *   </IntrinsicFlex>
 *
 * @see https://tishman.netlify.app/components/intrinsic-flex
 * @see https://theme-ui.com/components/flex/
 * @see https://theme-ui.com/components/aspect-ratio/
 * @see https://css-tricks.com/aspect-ratio-boxes/#article-header-id-6
 */
export const IntrinsicFlex = forwardRef<HTMLDivElement, IntrinsicFlexProps>(
  ({ ratio = 1, minWidth, maxWidth, ...props }, forwardedRef) => (
    <IntrinsicBox
      maxWidth={maxWidth}
      minWidth={minWidth}
      ratio={ratio}
      sx={{ display: 'flex' }}
    >
      <Box
        // Taken from https://github.com/system-ui/theme-ui/blob/57483e/packages/components/src/AspectRatio.js#L21
        __css={{ display: 'flex', flex: 1 }}
        ref={forwardedRef}
        {...props}
      />
    </IntrinsicBox>
  ),
);

IntrinsicFlex.displayName = 'IntrinsicFlex';
